import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  CalendarDays, ClipboardList, FileText, Wallet, CreditCard,
  ShieldCheck, BarChart3, Settings, Package,
} from "lucide-react";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PlaceholderPage from "@/pages/PlaceholderPage";
import Praticantes from "@/pages/Praticantes";
import PraticanteDetail from "@/pages/PraticanteDetail";
import FichaAvaliacao from "@/pages/FichaAvaliacao";
import Responsaveis from "@/pages/Responsaveis";
import Funcionarios from "@/pages/Funcionarios";
import CompleteProfile from "@/pages/CompleteProfile";
import Cavalos from "@/pages/Cavalos";
import Usuarios from "@/pages/Usuarios";
import ListaEspera from "@/pages/ListaEspera";
import Agenda from "@/pages/Agenda";
import Financeiro from "@/pages/Financeiro";
import Evolucoes from "@/pages/Evolucoes";
import Auditoria from "@/pages/Auditoria";
import Lixeira from "@/pages/Lixeira";

const placeholderModules = [
  { path: "documentos",    title: "Documentos",          icon: FileText,       phase: "Próxima entrega", roles: ["admin", "recepcionista"], description: "Upload de laudos, atestados, exames e fotos por cadastro." },
  { path: "estoque",       title: "Estoque",             icon: Package,        phase: "Próxima entrega", roles: ["admin"], description: "Ração, feno, suplementos, medicamentos e materiais de manejo." },
  { path: "convenios",     title: "Convênios",           icon: ShieldCheck,    phase: "Próxima entrega", roles: ["admin", "financeiro"], description: "Convênios/planos com tabela de valores." },
  { path: "relatorios",    title: "Relatórios",          icon: BarChart3,      phase: "Próxima entrega", roles: ["admin", "financeiro"], description: "Relatórios clínicos, operacionais e financeiros." },
  { path: "configuracoes", title: "Configurações",       icon: Settings,       phase: "Próxima entrega", roles: ["admin"], description: "Configurações gerais e integrações." },
];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/completar-perfil" element={
            <ProtectedRoute><CompleteProfile /></ProtectedRoute>
          } />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Praticantes */}
            <Route path="/praticantes" element={
              <ProtectedRoute allowedRoles={["admin", "recepcionista", "terapeuta", "financeiro"]}><Praticantes /></ProtectedRoute>
            } />
            <Route path="/praticantes/:id/ficha" element={
              <ProtectedRoute allowedRoles={["admin", "terapeuta"]}><FichaAvaliacao /></ProtectedRoute>
            } />
            <Route path="/praticantes/:id" element={
              <ProtectedRoute allowedRoles={["admin", "recepcionista", "terapeuta", "financeiro"]}><PraticanteDetail /></ProtectedRoute>
            } />

            {/* Redirect legacy /pacientes -> /praticantes */}
            <Route path="/pacientes" element={<Navigate to="/praticantes" replace />} />

            <Route path="/responsaveis" element={
              <ProtectedRoute allowedRoles={["admin", "recepcionista"]}><Responsaveis /></ProtectedRoute>
            } />
            <Route path="/terapeutas" element={<Navigate to="/funcionarios?cargo=terapeuta" replace />} />
            <Route path="/funcionarios" element={
              <ProtectedRoute allowedRoles={["admin", "financeiro"]}><Funcionarios /></ProtectedRoute>
            } />
            <Route path="/cavalos" element={
              <ProtectedRoute allowedRoles={["admin", "financeiro"]}><Cavalos /></ProtectedRoute>
            } />
            <Route path="/usuarios" element={
              <ProtectedRoute allowedRoles={["admin"]}><Usuarios /></ProtectedRoute>
            } />
            <Route path="/lista-espera" element={
              <ProtectedRoute allowedRoles={["admin", "recepcionista"]}><ListaEspera /></ProtectedRoute>
            } />

            {/* Agenda + Financeiro (Fase 4) */}
            <Route path="/agenda" element={
              <ProtectedRoute allowedRoles={["admin", "recepcionista", "terapeuta", "financeiro"]}><Agenda /></ProtectedRoute>
            } />
            <Route path="/financeiro" element={
              <ProtectedRoute allowedRoles={["admin", "financeiro"]}><Financeiro /></ProtectedRoute>
            } />
            <Route path="/pagamentos" element={
              <ProtectedRoute allowedRoles={["admin", "financeiro", "recepcionista"]}><Financeiro /></ProtectedRoute>
            } />
            <Route path="/evolucoes" element={
              <ProtectedRoute allowedRoles={["admin", "terapeuta"]}><Evolucoes /></ProtectedRoute>
            } />

            {/* Administração (Fase 5.2) */}
            <Route path="/auditoria" element={
              <ProtectedRoute allowedRoles={["admin"]}><Auditoria /></ProtectedRoute>
            } />
            <Route path="/lixeira" element={
              <ProtectedRoute allowedRoles={["admin"]}><Lixeira /></ProtectedRoute>
            } />

            {placeholderModules.map((m) => (
              <Route
                key={m.path}
                path={`/${m.path}`}
                element={
                  <ProtectedRoute allowedRoles={m.roles}>
                    <PlaceholderPage title={m.title} icon={m.icon} phase={m.phase} description={m.description} />
                  </ProtectedRoute>
                }
              />
            ))}

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
